/**
 * 서버 저장 언어(users.locale) → UI 언어 적용 훅.
 *
 * 사용자 언어의 SSOT 는 DB(users.locale)이고, 전달 경로는 GET /api/auth/me 다. 이 훅은 그 값을 받아
 * i18next/dayjs 에 적용하는 **단일 지점**이며, /auth/me 를 조회하는 곳(회사 앱 useMe, 어드민 콘솔 AdminGuard)이 공유함.
 *
 * 같은 값이 다시 와도(재조회) 재적용하지 않음 — 사용자 설정 화면의 미저장 미리보기를 되돌려버리지 않기 위함.
 *
 * 로그인 전 화면에서 직접 고른 언어(browserLang.ts)가 계정 언어와 다르면 계정 언어로 저장(PUT /api/account/settings)하고
 * 저장 응답의 locale 을 /auth/me 캐시에 넣어 적용함. 저장에 실패하면 계정 언어를 그대로 씀. 적용한 계정 언어는 브라우저에
 * 기억해 로그아웃 뒤 로그인 화면이 그 언어로 열리게 함.
 * 한 화면에서 이 훅이 여럿 돌 수 있음(AuthGuard = useMe + 게이트) — 저장 중에는 다른 훅이 계정 언어를 적용하지 않음.
 *
 * 로그인 후 화면은 {@link useServerLangGate} 로 서버 언어가 적용될 때까지 그리지 않는다. 브라우저 언어로 먼저 그리면
 * react-i18next(15.x)가 렌더~구독 사이의 언어 전환을 놓쳐 `t` 를 새로 만들지 않고, [t] 로 메모한 라벨이
 * 첫 렌더 언어로 남는다(2026-10-22 문서 목록 유형 필터가 영어 화면에서 한국어로 남던 결함).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { updateSettings } from "@/lib/account";
import { queryKeys } from "@/lib/query-keys";

import { rememberBrowserLang, takePickedLang } from "./browserLang";
import i18next, { isSupportedLng, type SupportedLng } from "./index";
import { applyLang } from "./useLang";

/** 계정 언어 적용 + 브라우저 기억(로그아웃 후 로그인 화면 언어). */
function applyAccountLang(locale: SupportedLng): void {
  applyLang(locale);
  rememberBrowserLang(locale);
}

/** 고른 언어를 계정에 저장하는 중인지 — 훅 인스턴스가 여럿이어도 저장은 한 번, 그동안 계정 언어 적용을 미룸. */
let savingPicked = false;

export function useApplyServerLang(locale: string | undefined): void {
  const appliedRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isSupportedLng(locale)) return;
    if (appliedRef.current === locale) return;
    appliedRef.current = locale;
    if (savingPicked) return;
    const picked = takePickedLang();
    if (!isSupportedLng(picked) || picked === locale) {
      applyAccountLang(locale);
      return;
    }
    savingPicked = true;
    applyLang(picked);
    void updateSettings({ locale: picked })
      .then((res) => {
        const saved = isSupportedLng(res.locale) ? res.locale : picked;
        savingPicked = false;
        applyAccountLang(saved);
        // 재조회 없이 캐시를 맞춤 — 게이트가 기다리는 locale 이 곧바로 저장값이 됨
        queryClient.setQueryData<{ locale?: string }>(queryKeys.me.current(), (old) =>
          old ? { ...old, locale: saved } : old,
        );
      })
      .catch(() => {
        savingPicked = false;
        applyAccountLang(locale);
      });
  }, [locale, queryClient]);
}

function subscribeLanguage(onChange: () => void): () => void {
  i18next.on("languageChanged", onChange);
  return () => i18next.off("languageChanged", onChange);
}

function languageSnapshot(): string {
  return i18next.language;
}

/** i18next 현재 언어. useSyncExternalStore 라 구독 전에 바뀐 언어도 놓치지 않음(구독 직후 스냅샷을 다시 읽음). */
export function useI18nLanguage(): string {
  return useSyncExternalStore(subscribeLanguage, languageSnapshot, languageSnapshot);
}

/**
 * 서버 언어 적용 + 화면 게이트. /auth/me 조회가 끝나고(settled — 성공·실패 모두) 서버 언어가 UI 에 적용될 때까지 false.
 * 조회 실패·미지원 언어면 기다리지 않음(현재 언어로 그림). 한 번 열리면 계속 true — 이후 설정 화면에서 언어를 저장해
 * /auth/me 캐시와 잠시 어긋나도 화면을 닫지 않는다(그때는 화면 컴포넌트가 이미 구독 중이라 전환을 받음).
 */
export function useServerLangGate(settled: boolean, locale: string | undefined): boolean {
  useApplyServerLang(locale);
  const current = useI18nLanguage();
  const ready = settled && (!isSupportedLng(locale) || current === locale);
  const [opened, setOpened] = useState(false);
  // 이전 렌더 정보 저장 패턴(렌더 중 자기 상태 갱신) — 한 번 열린 게이트를 고정.
  if (ready && !opened) setOpened(true);
  return opened || ready;
}
